import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadProfile, signOut, updateName, useSession } from '../lib/session';
import Flag from '../components/Flag';
import { MAX_DISPLAY_NAME } from '@mv/contracts';
import { setTheme, useTheme } from '../lib/theme';
import { clearPinnedClock, isClockPinned } from '../lib/time';

// The build stamp used to live here. It is in the site footer now (`App.tsx`),
// which this screen renders anyway through `Shell` — so keeping a copy would
// have shown it twice on the one page it was originally added for.

/** Deliberately thin. Four days, then never opened again. */
export default function Profile() {
  const session = useSession();

  // An organiser may have moved this guest to another team (D54). Since D55 that
  // is one read rather than a forced token refresh, and it is cheap enough to do
  // on the screen that states a team outright. Fire-and-forget: the page paints
  // from the cached profile and re-renders if `/me` disagrees.
  useEffect(() => { void loadProfile(); }, []);
  const navigate = useNavigate();
  const { choice } = useTheme();
  const [name, setName] = useState(session?.name ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  /**
   * Whether the guest has typed since the field was last in step with the
   * session. It gates both halves of the fix below, and without it this screen
   * can *destroy* a name rather than fail to save one.
   *
   * `name` is seeded once from `session.name`; `loadProfile()` corrects
   * `session.name` a moment later. On the deploy of D55 that correction fires
   * for **every already-signed-in guest**, because none of them have an
   * `mv.profile` cache yet and `fromToken` falls back to "Guest". A field still
   * holding the stale value, blurred by any tap in and out, would then PATCH
   * "Guest" over their real name and walk every photograph they have uploaded
   * rewriting `uploaderName`. Silently, with a 200.
   */
  const dirty = useRef(false);

  // Follow the session while the guest is not mid-edit. The `dirty` guard is
  // what stops a slow `/me` landing on top of what somebody is typing.
  useEffect(() => {
    if (!dirty.current) setName(session?.name ?? '');
  }, [session?.name]);

  if (!session) return null;

  const providerLine = session.provider === 'google'
    ? 'You signed in with Google.'
    : session.provider === 'admin'
      ? 'You signed in as an organiser.'
      : 'You signed in with a code sent to your email.';

  return (
    <div className="page page-pad page-narrow page-narrow-sm" style={{ paddingTop: 20 }}>
      <h2 style={{ margin: '0 0 16px' }}>You</h2>

      <div className="field">
        <label htmlFor="name">Your name, as it shows on your photos</label>
        <input
          id="name" className="input" style={{ minHeight: 48, fontSize: 16 }}
          value={name}
          // Clear on the first keystroke, not on the next blur: a guest who is
          // already retyping has read the message, and leaving it under text
          // that may now be valid is the app arguing with what it can see.
          // Capped here rather than with `maxLength`, which counts UTF-16
          // units while `cleanDisplayName` on the server counts code points.
          // The mismatch only ever made the client stricter — nothing invalid
          // could reach the API — but it cut an emoji-heavy name several
          // characters short of what the API would have taken, which at a
          // wedding with guests writing in three scripts is the wrong side to
          // be wrong on.
          onChange={(e) => {
            dirty.current = true;
            setName([...e.target.value].slice(0, MAX_DISPLAY_NAME).join(''));
            setNameError(null);
          }}
          onBlur={async () => {
            // Nothing typed, nothing to save. Belt and braces with the sync
            // above: the write is what does the damage, so it does not happen
            // at all unless the guest actually edited something.
            if (!dirty.current) return;
            // Reverts the field if the write failed, so it never shows a name
            // the gallery does not have.
            const res = await updateName(name);
            dirty.current = false;
            setNameError(res.ok ? null : res.error ?? 'That name did not save.');
            // Either way the field ends up showing what the gallery shows: the
            // server's cleaned value on success, the previous name on failure.
            if (res.name) setName(res.name);
            else if (!res.ok) setName(session?.name ?? '');
          }}
        />
        {nameError && (
          <p role="alert" style={{ fontSize: 12, color: 'var(--color-accent-700)', margin: '6px 0 0' }}>
            {nameError}
          </p>
        )}
      </div>

      <div className="card" style={{ marginTop: 16, background: 'var(--color-accent-2-100)', gap: 6 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--color-accent-2-800)' }}>
          Your team
        </div>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 28, color: 'var(--color-accent-2-900)' }}>
          <Flag team={session.team} height={22} decorative /> {session.team}
        </div>
      </div>

      <div className="card" style={{ marginTop: 12, gap: 4 }}>
        <div style={{ fontSize: 13 }}>{providerLine}</div>
        <div style={{ fontSize: 13, opacity: 0.75 }}>
          Come back on another phone with <strong>{session.email}</strong> and everything of yours is still yours.
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <div className="field"><label>Light or dark</label></div>
        <div className="seg">
          {(['system', 'light', 'dark'] as const).map((option) => (
            <label key={option} className="seg-opt">
              <input type="radio" name="theme" checked={choice === option} onChange={() => setTheme(option)} />
              {option === 'system' ? 'Match my phone' : option === 'light' ? 'Light' : 'Dark'}
            </label>
          ))}
        </div>
      </div>

      {session.isAdmin && (
        /* Organisers only — group membership on an ordinary account (D48).
           D37 says no admin console and this is still not one: featuring happens
           on a photo's own screen, and the guest list below is read-only. */
        <div className="card" style={{ marginTop: 16, background: 'var(--color-surface)', gap: 8 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.6 }}>
            Organiser
          </div>
          <p style={{ fontSize: 13, margin: 0, opacity: 0.85 }}>
            You can feature and unfeature any photo from its own screen, and the gallery
            has a Featured filter. You cannot delete anything — that stays with whoever
            took it.
          </p>
          <p style={{ fontSize: 13, margin: 0, opacity: 0.85 }}>
            The reception slideshow starts from the <Link to="/gallery">Photos</Link> tab.
          </p>
          <Link
            className="btn btn-secondary" to="/me/guests"
            style={{ minHeight: 44, marginTop: 4, background: 'var(--color-bg)' }}
          >
            Who has signed in
          </Link>
        </div>
      )}

      <div className="stack" style={{ gap: 10, marginTop: 24 }}>
        <Link to="/credits" style={{ fontSize: 14 }}>Everyone who made this week</Link>
        <p style={{ fontSize: 12, opacity: 0.55, margin: 0 }}>
          You can add this page to your home screen from your browser's share menu, if you'd rather not go
          hunting for it in a tab. Entirely optional.
        </p>
      </div>

      <button
        type="button" className="btn btn-secondary btn-block" style={{ minHeight: 48, marginTop: 24 }}
        onClick={() => { signOut(); navigate('/', { replace: true }); }}
      >
        Sign out
      </button>

      {isClockPinned() && (
        <button type="button" className="btn btn-ghost btn-block" onClick={() => { clearPinnedClock(); window.location.reload(); }}>
          The clock is pinned for a demo — put it back
        </button>
      )}

    </div>
  );
}
